import { CODE_WORLD_SLUGS, type Lesson, type ServedWorld } from '@vrlearning/lesson-schema';
import { MEDIA_CACHE } from '../media-cache.js';
import { WORLDS_CACHE, mayPrefetch } from '../render/worlds/cache.js';
import { MANIFEST, worldFiles, worldUrl, type Manifest } from '../render/worlds/manifest.js';
import type { WorldFile } from './plan.js';
import type { RunnerDeps } from './runner.js';

/**
 * The prefetcher's parts in a browser: the page's fetch (so through the service worker, which
 * keeps what it fetched), Cache Storage, the storage estimate and SubtleCrypto.
 */

/** A lesson's world files, from the manifest, with the size and hash each is stored under. */
export function lessonWorldFiles(
  lesson: Pick<Lesson, 'scene'>,
  manifest: Manifest = MANIFEST,
  base?: string,
  served?: Readonly<Record<string, ServedWorld>>,
): WorldFile[] {
  if (lesson.scene.type !== 'world') return [];
  const slug = lesson.scene.world;
  // Only the code worlds have files in the manifest; a package's come with its lesson (media).
  if (!(CODE_WORLD_SLUGS as readonly string[]).includes(slug)) return [];
  // A served package replaces the code world: its files are media, planned by packageMedia (ADR-049).
  if (served?.[slug]) return [];
  return worldFiles(slug, manifest).map((file) => ({
    url: base === undefined ? worldUrl(file.stored) : worldUrl(file.stored, base),
    bytes: file.bytes,
    // Content-addressed (ADR-032): the name is the bytes' SHA-256.
    sha256: file.stored.split('.')[0]!,
  }));
}

/** SHA-256 as lowercase hex; undefined where the page has no SubtleCrypto (an insecure context). */
export function hexDigest(
  subtle: SubtleCrypto | undefined = globalThis.crypto?.subtle,
): ((bytes: ArrayBuffer) => Promise<string>) | undefined {
  if (!subtle) return undefined;
  return async (bytes) => {
    const hash = new Uint8Array(await subtle.digest('SHA-256', bytes));
    return Array.from(hash, (byte) => byte.toString(16).padStart(2, '0')).join('');
  };
}

type Connection = {
  addEventListener(type: string, listener: () => void, options?: AddEventListenerOptions): void;
  removeEventListener(type: string, listener: () => void): void;
};

/**
 * Resolves at the connection's next `change` (Wi-Fi back, data saver off), or when `signal`
 * aborts; either way no listener is left behind.
 */
export function nextChange(connection: Connection, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    const done = (): void => {
      connection.removeEventListener('change', done);
      signal.removeEventListener('abort', done);
      resolve();
    };
    connection.addEventListener('change', done, { once: true });
    signal.addEventListener('abort', done, { once: true });
  });
}

export function browserRunnerDeps(refresh: RunnerDeps['refresh']): Omit<RunnerDeps, 'report'> {
  const storage = globalThis.caches as CacheStorage | undefined;
  const connection = (globalThis.navigator as (Navigator & { connection?: Connection }) | undefined)
    ?.connection;
  const estimate = globalThis.navigator?.storage?.estimate?.bind(globalThis.navigator.storage);
  return {
    fetch: (url, signal) => fetch(url, signal ? { signal } : {}),
    cache: {
      match: async (item) => (storage ? (await storage.match(item.key)) !== undefined : false),
      read: async (item) => {
        const response = storage ? await storage.match(item.key) : undefined;
        return response ? response.arrayBuffer() : undefined;
      },
      delete: async (item) => {
        if (!storage) return;
        const cache = await storage.open(item.kind === 'world' ? WORLDS_CACHE : MEDIA_CACHE);
        await cache.delete(item.key);
      },
    },
    sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    random: () => Math.random(),
    now: () => Date.now(),
    estimate,
    digest: hexDigest(),
    refresh,
    mayPrefetch: () => mayPrefetch(),
    ...(connection ? { connectionChanged: (signal) => nextChange(connection, signal) } : {}),
  };
}
